// src/pages/Attrition/AttritionUpload.jsx
// Upload: empCode | mobile | empEmail | resignedDate | reason
// Result: per-row status + Cohort / Month / Division summary

import React, { useState } from "react";
import { useNavigate }     from "react-router-dom";
import {
  Button, Table, Tag, Steps, Alert,
  message as antMsg, Progress,
} from "antd";
import {
  ArrowLeftOutlined, DownloadOutlined, UploadOutlined,
  CheckCircleOutlined, WarningOutlined, UserDeleteOutlined,
  FileExcelOutlined, EyeOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import api from "../../services/api";

const C = {
  navy:"#1B2D6B", blue:"#1E6FBF", red:"#dc2626",
  green:"#16a34a", amber:"#b45309", purple:"#7c3aed",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  border:"#dce6f0", bg:"#f5f8fc",
};

const TEMPLATE_COLS = ["empCode","mobile","empEmail","resignedDate","reason"];

function safeStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "object" && v.text  != null) return String(v.text).trim();
  if (typeof v === "object" && v.result != null) return String(v.result).trim();
  if (Array.isArray(v?.richText)) return v.richText.map(r=>r.text||"").join("").trim();
  return String(v).trim();
}

function parseDate(v) {
  if (!v) return "";
  if (v instanceof Date) return dayjs(v).format("YYYY-MM-DD");
  if (typeof v === "number") return dayjs(new Date(Math.round((v-25569)*86400*1000))).format("YYYY-MM-DD");
  const s = safeStr(v);
  if (!s || s==="-") return "";
  const d = dayjs(s);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
}

// ── Summary card ─────────────────────────────────────────────────
function SummaryTable({ title, data, color }) {
  if (!data?.length) return null;
  return (
    <div style={{
      background:"#fff", border:`1px solid ${C.border}`,
      borderRadius:12, overflow:"hidden", flex:1, minWidth:260,
    }}>
      <div style={{
        padding:"10px 16px", fontWeight:700, fontSize:13,
        color:"#fff", background:color,
      }}>{title}</div>
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <thead>
          <tr style={{background:C.bg}}>
            <th style={{padding:"7px 14px",textAlign:"left",fontSize:11,
              fontWeight:700,color:C.text3,textTransform:"uppercase",
              letterSpacing:"0.5px",borderBottom:`1px solid ${C.border}`}}>Name</th>
            <th style={{padding:"7px 14px",textAlign:"center",fontSize:11,
              fontWeight:700,color:C.text3,textTransform:"uppercase",
              letterSpacing:"0.5px",borderBottom:`1px solid ${C.border}`,width:80}}>Resigned</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r,i)=>(
            <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
              <td style={{padding:"7px 14px",fontSize:13,color:C.text1}}>{r.name}</td>
              <td style={{padding:"7px 14px",textAlign:"center"}}>
                <Tag style={{borderRadius:20,fontWeight:700,fontSize:12,
                  color,background:color+"18",border:`1px solid ${color}44`,margin:0}}>
                  {r.count}
                </Tag>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
export default function AttritionUpload() {
  const navigate = useNavigate();

  const [step,        setStep]       = useState(0);
  const [file,        setFile]       = useState(null);
  const [parsing,     setParsing]    = useState(false);
  const [rows,        setRows]       = useState([]);
  const [loading,     setLoading]    = useState(false);
  const [result,      setResult]     = useState(null);
  const [error,       setError]      = useState("");

  // ── Parse file ───────────────────────────────────────────────
  function parseFile(f) {
    setParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb  = XLSX.read(e.target.result, { type:"array", cellDates:true });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval:"", raw:false });

        const parsed = raw.map((r, idx) => {
          const empCode     = safeStr(r.empCode     || r.EmpCode     || r.emp_code || "").toUpperCase();
          const mobile      = safeStr(r.mobile      || r.Mobile      || "");
          const empEmail    = safeStr(r.empEmail     || r.Email       || r.email   || "").toLowerCase();
          const resignedDate= parseDate(r.resignedDate || r.ResignedDate || r.resigned_date || "");
          const reason      = safeStr(r.reason      || r.Reason      || "");

          const errs = [];
          if (!empCode && !mobile && !empEmail) errs.push("empCode, mobile or email required");
          if (!resignedDate) errs.push("resignedDate required");

          return { _row:idx+2, empCode, mobile, empEmail, resignedDate, reason, _errors:errs };
        }).filter(r => r.empCode||r.mobile||r.empEmail);

        setRows(parsed);
        setFile(f);
        setStep(1);
      } catch {
        antMsg.error("Failed to read file");
      } finally { setParsing(false); }
    };
    reader.readAsArrayBuffer(f);
  }

  // ── Submit ───────────────────────────────────────────────────
  async function handleSubmit() {
    const valid = rows.filter(r => !r._errors?.length);
    if (!valid.length) { antMsg.warning("No valid rows"); return; }
    setLoading(true); setError("");
    try {
      const res = await api.post("/employees/bulk-attrition", {
        rows: valid.map(({ empCode, mobile, empEmail, resignedDate, reason }) =>
          ({ empCode, mobile, empEmail, resignedDate, reason })
        ),
      });
      const payload = res.data?.data ?? res.data;
      setResult(payload);
      setStep(2);
      antMsg.success(`Done — ${payload?.updated ?? 0} employees marked resigned`);
    } catch(err) {
      const msg = err?.response?.data?.message || err.message || "Connection failed";
      setError(msg); setStep(2);
    } finally { setLoading(false); }
  }

  // ── Download template ────────────────────────────────────────
  function downloadTemplate() {
    const ws = XLSX.utils.json_to_sheet([
      { empCode:"L04480", mobile:"9876543210", empEmail:"john@eris.com", resignedDate:"2026-03-20", reason:"Better opportunity" },
      { empCode:"E14059", mobile:"9876543211", empEmail:"",              resignedDate:"2026-03-21", reason:"Personal" },
    ]);
    ws["!cols"] = [{wch:12},{wch:14},{wch:30},{wch:14},{wch:25}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attrition");
    XLSX.writeFile(wb, "Attrition_Template.xlsx");
    antMsg.success("Template downloaded!");
  }

  // ── Preview columns ──────────────────────────────────────────
  const previewCols = [
    { title:"#",           dataIndex:"_row",        width:50,
      render:v=><span style={{color:C.text3,fontSize:11}}>{v}</span> },
    { title:"Emp Code",    dataIndex:"empCode",      width:100,
      render:v=>v?<span style={{fontFamily:"monospace",fontWeight:700,color:C.navy}}>{v}</span>
               :<span style={{color:C.text3}}>—</span> },
    { title:"Mobile",      dataIndex:"mobile",       width:120,
      render:v=>v||<span style={{color:C.text3}}>—</span> },
    { title:"Email",       dataIndex:"empEmail",     width:200,
      render:v=>v||<span style={{color:C.text3}}>—</span> },
    { title:"Resign Date", dataIndex:"resignedDate", width:120,
      render:v=>v
        ?<Tag color="red" style={{borderRadius:20,fontWeight:600}}>{dayjs(v).format("DD MMM YYYY")}</Tag>
        :<Tag color="warning">Missing</Tag> },
    { title:"Reason",      dataIndex:"reason",       width:180,
      render:v=>v||<span style={{color:C.text3}}>—</span> },
    { title:"Status",      dataIndex:"_errors",      width:110, fixed:"right",
      render:e=>e?.length
        ?<Tag color="error">{e.length} error{e.length>1?"s":""}</Tag>
        :<Tag color="success">✓ Ready</Tag> },
  ];

  const validCount   = rows.filter(r=>!r._errors?.length).length;
  const invalidCount = rows.filter(r=> r._errors?.length).length;

  return (
    <div style={{maxWidth:1000,margin:"0 auto",padding:"0 4px 60px"}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24}}>
        <Button icon={<ArrowLeftOutlined />} onClick={()=>navigate("/attrition")} />
        <div>
          <h2 style={{fontSize:20,fontWeight:700,color:C.text1,margin:0,display:"flex",
            alignItems:"center",gap:10}}>
            <UserDeleteOutlined style={{color:C.red}}/> Attrition Upload
          </h2>
          <p style={{fontSize:13,color:C.text3,margin:0}}>
            Upload: Emp Code / Mobile / Email + Resigned Date + Reason
          </p>
        </div>
      </div>

      {/* Steps */}
      <Steps current={step} style={{marginBottom:28}} items={[
        {title:"Select File",  description:"Upload Excel"},
        {title:"Preview",      description:"Review before update"},
        {title:"Done",         description:"Summary"},
      ]} />

      {/* ══ STEP 0 ══ */}
      {step === 0 && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div
            style={{border:`2px dashed ${C.border}`,borderRadius:16,background:"#fafcff",
              padding:"50px 40px",textAlign:"center",cursor:"pointer"}}
            onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=C.blue;}}
            onDragLeave={e=>{e.currentTarget.style.borderColor=C.border;}}
            onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor=C.border;
              const f=e.dataTransfer.files[0];if(f)parseFile(f);}}
            onClick={()=>document.getElementById("attr-file-inp").click()}
          >
            <input id="attr-file-inp" type="file" accept=".xlsx,.xls"
              style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)parseFile(f);}} />
            {parsing
              ? <div style={{color:C.text3,fontSize:14}}>📖 Reading file...</div>
              : <>
                  <div style={{fontSize:44,marginBottom:12}}>📂</div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text1,marginBottom:6}}>
                    Click or drag Excel file here
                  </div>
                  <div style={{fontSize:12,color:C.text3}}>Supports .xlsx · Max 10MB</div>
                </>
            }
          </div>

          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:14,
            padding:"18px 22px",display:"flex",justifyContent:"space-between",
            alignItems:"flex-start",flexWrap:"wrap",gap:16}}>
            <div>
              <div style={{fontWeight:700,fontSize:14,color:C.text1,marginBottom:10}}>
                📋 Template columns:
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
                <Tag color="blue">empCode</Tag>
                <Tag color="blue">mobile</Tag>
                <Tag color="blue">empEmail</Tag>
                <Tag color="red">resignedDate *</Tag>
                <Tag color="default">reason</Tag>
              </div>
              <div style={{fontSize:12,color:C.text3}}>
                * Provide at least one of: <strong>empCode</strong>, <strong>mobile</strong>, <strong>empEmail</strong><br/>
                * <strong>resignedDate</strong> required — format: YYYY-MM-DD or DD/MM/YYYY
              </div>
            </div>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              Download Template (.xlsx)
            </Button>
          </div>

          <Alert type="warning" showIcon message="Important"
            description="Once marked resigned, all call logging and editing is blocked for that employee."
            style={{borderRadius:12}} />
        </div>
      )}

      {/* ══ STEP 1 — Preview ══ */}
      {step === 1 && (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,
            padding:"14px 20px",display:"flex",alignItems:"center",
            justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <FileExcelOutlined style={{fontSize:28,color:C.green}} />
              <div>
                <div style={{fontWeight:700,fontSize:14,color:C.text1}}>{file?.name}</div>
                <div style={{fontSize:12,color:C.text3}}>{rows.length} rows read</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <Tag color="success" style={{fontSize:13,padding:"4px 12px",borderRadius:20}}>
                ✓ {validCount} ready
              </Tag>
              {invalidCount>0&&(
                <Tag color="error" style={{fontSize:13,padding:"4px 12px",borderRadius:20}}>
                  ✗ {invalidCount} errors
                </Tag>
              )}
            </div>
          </div>

          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,
              fontWeight:600,fontSize:13,color:C.text1}}>
              <EyeOutlined style={{marginRight:8,color:C.blue}}/>Preview — {rows.length} rows
            </div>
            <Table dataSource={rows} columns={previewCols} rowKey="_row" size="small"
              scroll={{x:900}}
              pagination={{pageSize:10,showTotal:t=>`${t} rows`}}
              rowClassName={r=>r._errors?.length?"bulk-row-error":""} />
          </div>

          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,
            padding:"14px 20px",display:"flex",justifyContent:"space-between",
            alignItems:"center",flexWrap:"wrap",gap:12}}>
            <div style={{fontSize:13,color:C.text2}}>
              {invalidCount>0
                ?`⚠️ ${invalidCount} rows will be skipped`
                :"✅ All rows valid — ready to process"}
            </div>
            <div style={{display:"flex",gap:10}}>
              <Button onClick={()=>{setFile(null);setRows([]);setStep(0);}}>
                ← Choose Different File
              </Button>
              <Button type="primary" danger icon={<UserDeleteOutlined />}
                loading={loading} disabled={validCount===0} onClick={handleSubmit}
                style={{minWidth:220,fontWeight:600}}>
                {loading?"Processing...":`Mark ${validCount} Employees as Resigned`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ══ STEP 2 — Result + Summary ══ */}
      {step === 2 && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {error ? (
            <Alert type="error" showIcon message="Upload Failed" description={error}
              style={{borderRadius:12}} />
          ) : (
            <>
              {/* KPI cards */}
              <div style={{background:"#fff",border:`1px solid ${C.border}`,
                borderRadius:16,padding:"24px",textAlign:"center"}}>
                <div style={{fontSize:40,marginBottom:8}}>
                  {result?.notFound===result?.totalRows?"❌":result?.updated>0?"✅":"⚠️"}
                </div>
                <div style={{fontSize:18,fontWeight:800,color:C.text1,marginBottom:16}}>
                  Attrition Update Complete
                </div>
                <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
                  {[
                    {label:"Total Rows",  value:result?.totalRows,         color:C.blue,   bg:"#eff6ff"},
                    {label:"Updated",     value:result?.updated,           color:C.green,  bg:"#f0fdf4"},
                    {label:"Not Found",   value:result?.notFound,          color:C.amber,  bg:"#fffbeb"},
                    {label:"Errors",      value:result?.errors?.length??0, color:C.red,    bg:"#fef2f2"},
                  ].map(s=>(
                    <div key={s.label} style={{padding:"14px 22px",borderRadius:12,minWidth:100,
                      background:s.bg,border:`1px solid ${s.color}33`}}>
                      <div style={{fontSize:28,fontWeight:800,color:s.color,lineHeight:1}}>{s.value??0}</div>
                      <div style={{fontSize:11,fontWeight:700,color:C.text3,textTransform:"uppercase",
                        letterSpacing:"0.6px",marginTop:4}}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Summary tables ── */}
              {result?.summary && (
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:C.text1,marginBottom:12}}>
                    📊 Resignation Summary
                  </div>
                  <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                    <SummaryTable
                      title="📅 Month-Year Wise"
                      data={result.summary.byMonth}
                      color={C.blue} />
                    <SummaryTable
                      title="🏢 Division Wise"
                      data={result.summary.byDivision}
                      color={C.purple} />
                    <SummaryTable
                      title="👥 Cohort Wise"
                      data={result.summary.byCohort}
                      color={C.amber} />
                  </div>
                </div>
              )}

              {/* Error rows */}
              {result?.errors?.length>0&&(
                <div style={{background:"#fff",border:"1.5px solid #fca5a5",
                  borderRadius:14,overflow:"hidden"}}>
                  <div style={{padding:"12px 16px",borderBottom:"1px solid #fecaca",
                    fontWeight:700,fontSize:13,color:C.red,background:"#fef2f2"}}>
                    <WarningOutlined style={{marginRight:8}}/>
                    Row Issues ({result.errors.length})
                  </div>
                  <Table dataSource={result.errors} rowKey="row" size="small"
                    pagination={{pageSize:10}}
                    columns={[
                      {title:"Row",        dataIndex:"row",        width:60},
                      {title:"Identifier", dataIndex:"identifier", width:160,
                        render:v=><span style={{fontFamily:"monospace",fontWeight:600}}>{v}</span>},
                      {title:"Reason",     dataIndex:"reason",
                        render:v=><Tag color="error" style={{whiteSpace:"normal"}}>{v}</Tag>},
                    ]} />
                </div>
              )}
            </>
          )}

          <div style={{display:"flex",gap:12}}>
            <Button onClick={()=>{setStep(0);setFile(null);setRows([]);setResult(null);setError("");}}>
              Upload Another File
            </Button>
            <Button type="primary" style={{background:C.navy}}
              onClick={()=>navigate("/attrition")}>
              Go to Employees
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}